import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    Avatar,
    Typography,
    Box,
    IconButton,
    Paper,
    Chip,

} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import PndRestApi from "src/utils/pnd/PndRestApi";
import { showNotification } from "src/shared/components/Notification";
import { ERROR_MESSAGES, HUB_MANAGER_ROLE_ID, HUB_OPERATOR_ROLE_ID, MERCHANT_ROLE_ID, RIDER_ROLE_ID } from "src/utils/pnd/PndConstants";
import moment from 'moment';

const OrderRemarksDetailsModel = ({ modalShow, setModalShow, isTitle, isOrderIdRemarks }) => {
    const [isRemarks, setRemarks] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (modalShow) {
            getAllOrderRemarkDetails();
        }
    }, [modalShow, isOrderIdRemarks]);

    const handleClose = () => {
        setModalShow(false);
    };

    const getAllOrderRemarkDetails = async () => {
        setLoading(true);
        try {
            const response = await PndRestApi.GetAllOrderRemarksByOrderId(isOrderIdRemarks);
            if (response.status === 200 && response.data.status === 200) {
                setRemarks(response.data.data);
            } else if (response.status === 200 && response.data.status !== 204) {
                showNotification("theme-light", "ltr", "Error", response.data.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification("theme-light", "ltr", "Error", ERROR_MESSAGES.tryCatchError);
        } finally {
            setLoading(false);
        }
    };

    const getRoleInfo = (roleId) => {
        switch (Number(roleId)) {
            case HUB_OPERATOR_ROLE_ID: return { label: 'Hub Operator', color: '#0ea5e9', bgColor: '#e0f2fe' }; // Light Blue
            case HUB_MANAGER_ROLE_ID: return { label: 'Hub Manager', color: '#6366f1', bgColor: '#eef2ff' }; // Indigo
            case MERCHANT_ROLE_ID: return { label: 'Merchant', color: '#ec4899', bgColor: '#fce7f3' }; // Pink
            case RIDER_ROLE_ID: return { label: 'Rider', color: '#f59e0b', bgColor: '#fef3c7' }; // Amber
            default: return { label: 'System', color: '#64748b', bgColor: '#f1f5f9' }; // Slate
        }
    };

    return (
        <Dialog
            open={modalShow}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }
            }}
        >
            <DialogTitle sx={{
                m: 0,
                p: '20px 24px',
                bgcolor: '#ffffff',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'sticky',
                top: 0,
                zIndex: 10
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 40, height: 40 }}>
                        <CommentIcon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                            {isTitle || "Order Remarks"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                            {isRemarks.length} {isRemarks.length === 1 ? 'Remark' : 'Remarks'} found
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: '#94a3b8',
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: '#f1f5f9',
                            color: '#ef4444'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: '#f8fafc', minHeight: '300px' }}>
                {isRemarks.length > 0 ? (
                    <List sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {isRemarks.map((item, index) => {
                            const role = getRoleInfo(item.userTypeId);
                            return (
                                <React.Fragment key={index}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: '16px',
                                            border: '1px solid #e2e8f0',
                                            bgcolor: '#ffffff',
                                            position: 'relative',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            {/* Role Indicator Strip */}
                                            <Box
                                                sx={{
                                                    width: '4px',
                                                    alignSelf: 'stretch',
                                                    bgcolor: role.color,
                                                    borderRadius: '4px',
                                                    flexShrink: 0
                                                }}
                                            />

                                            <Box sx={{ flexGrow: 1 }}>
                                                {/* Header: Role & Time */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                                    <Chip
                                                        label={role.label}
                                                        size="small"
                                                        sx={{
                                                            height: 24,
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700,
                                                            color: role.color,
                                                            bgcolor: role.bgColor,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px'
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                                                        {item.createdOn ? moment(item.createdOn).fromNow() : ''}
                                                    </Typography>
                                                </Box>

                                                {/* Remark Text */}
                                                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6, fontSize: '0.9rem', fontWeight: 500 }}>
                                                    {item.remark}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </React.Fragment>
                            );
                        })}
                    </List>
                ) : (
                    <Box sx={{
                        p: 8,
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        mt: 4
                    }}>
                        <Avatar sx={{ width: 80, height: 80, bgcolor: '#f1f5f9', mb: 2 }}>
                            <CommentIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600, mb: 1 }}>
                            No Remarks Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8', maxWidth: '250px', lineHeight: 1.5 }}>
                            There are currently no remarks attached to this order.
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

OrderRemarksDetailsModel.propTypes = {
    modalShow: PropTypes.bool.isRequired,
    setModalShow: PropTypes.func.isRequired,
    isTitle: PropTypes.string,
    isOrderIdRemarks: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default OrderRemarksDetailsModel;
